import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  api,
  clearCityToken,
  LANG_KEY,
  onCityTokenExpired,
  setCityToken,
  TENANT_ID_KEY,
  TENANT_KEY,
  TOKEN_KEY,
} from "@/lib/api";
import { loadDictionary, translate } from "@/i18n/dictionaries";
import { applyTenantTheme } from "@/theme/tenantTheme";
import { applyCityBranding } from "@/lib/pwa";
import { PLATFORM_PATHS, firstSegment, resolveCityFromUrl } from "@/lib/cityPath";
import { consumeWebHandoff } from "@/lib/webHandoff";
import { isAdminHost } from "@/lib/adminHost";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // First-run default language. A stored preference (set when the user flips the language toggle)
  // always wins. With none: the CITIZEN app is Hindi-first (C1 P0 — the audience is Hindi-first even
  // on an English-set budget Android, so it defaults to Hindi, not the browser locale). The platform
  // ADMIN console is staff-facing and English-first, so on the admin host an unset preference
  // defaults to English — otherwise an English-locale operator's login rendered in Hindi. The
  // host match is the shared `isAdminHost()` (exact hostname === REACT_APP_ADMIN_HOST); the
  // toggle stays reachable on the first screen either way.
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored) return stored;
    if (isAdminHost()) return "en";
    return "hi";
  });
  const [tenants, setTenants] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [theme, setTheme] = useState(null);
  const [mode, setMode] = useState("city_portal");
  const [businessContext, setBusinessContext] = useState(null);
  // White-label branding for the in-app storefront (`/b/:slug`, `/:slug`) — published by the
  // Storefront page when the business is premium + branded. `businessContext` covers the
  // custom-domain path instead; the header picks whichever is present (see PR5 wiring decision).
  const [storefrontBranding, setStorefrontBranding] = useState(null);
  // F4 (L2-004 ISP coverage-check, L2-006 membership "Join"): a one-shot prefill for
  // `EnquirySection`'s message field, set by another storefront block right before it scrolls to
  // the enquiry section — same shape/lifecycle idea as `storefrontBranding` above (page-level
  // state, not prop-drilled through `sectionsFor`'s generic `<s.Component business={business} />`
  // render, since a block that sets it and the enquiry section that reads it are siblings with no
  // parent that knows about either). `EnquirySection` clears it once consumed so it never bleeds
  // into a later, unrelated visit to the same section.
  const [enquiryPrefill, setEnquiryPrefill] = useState(null);
  // D2 (storefront S3): same one-shot prefill idea as `enquiryPrefill` above, for the hotel
  // "Dining" fulfilment-mode chips (`MenuSection.jsx`) to hand the checkout page
  // (`pages/Cart.jsx`) which mode the visitor picked before adding anything to the cart. Cart.jsx
  // consumes and clears it exactly once, same lifecycle as the enquiry prefill.
  const [fulfillmentPrefill, setFulfillmentPrefill] = useState(null);
  // Global capability flags from the resolve payload's `app_config.features`. Read at load so a
  // disabled feature draws no screen and fires no probe request (e.g. subscription billing).
  const [features, setFeatures] = useState({});
  const [primaryDomain, setPrimaryDomain] = useState("");
  const [auth, setAuth] = useState(null);
  const [cart, setCart] = useState(null);
  // The active language's strings. Held as state rather than read from a module cache so React
  // can actually see when they arrive — see the note on `translate` in i18n/dictionaries.js.
  const [dict, setDict] = useState(null);
  const [booting, setBooting] = useState(true);
  const [bootSlow, setBootSlow] = useState(false);
  const [bootError, setBootError] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);
  const [citySlug, setCitySlug] = useState("");

  const t = useCallback((key, vars) => translate(dict, key, vars), [dict]);

  // U13: the directory categories are national reference data, yet City Home and Directory each
  // re-fetched them on every visit. One promise per session AND city, shared by every caller; a
  // failure is dropped so the next call retries. RF-11: each row now also carries the city's
  // `listing_count`, so the cache is keyed by tenant (a city switch must not reuse another city's
  // counts). Resolves to the same axios-style `{ data }` response `api.get` would.
  const categoriesRef = useRef(null);
  const getCategories = useCallback(() => {
    const key = tenant?.id || "";
    if (!categoriesRef.current || categoriesRef.current.key !== key) {
      const promise = api.get("/directory/categories").catch((err) => {
        if (categoriesRef.current?.promise === promise) categoriesRef.current = null;
        throw err;
      });
      categoriesRef.current = { key, promise };
    }
    return categoriesRef.current.promise;
  }, [tenant?.id]);

  // Fetch the new language BEFORE switching to it. Switching first would render every string as
  // its own key for the duration of the chunk fetch — a screen of `nav.directory` where the words
  // used to be. The old language stays on screen until the new one can replace it.
  const changeLanguage = useCallback(async (next) => {
    try {
      await loadDictionary(next);
    } catch {
      // A failed chunk fetch leaves the current language in place, which is the safe outcome.
      return;
    }
    setDict(await loadDictionary(next));
    setLang(next);
    localStorage.setItem(LANG_KEY, next);
    document.documentElement.lang = next;
  }, []);

  const loadTenant = useCallback(async (slug, signal, prefetched) => {
    const params = { host: window.location.hostname };
    if (slug) params.tenant = slug;
    // `prefetched` is the boot's speculative resolve (fired alongside `/tenants`); only trusted
    // when it is for the very city now being committed, otherwise resolve afresh as before.
    const data =
      prefetched?.tenant?.slug === slug && slug
        ? prefetched
        : (await api.get("/tenants/resolve", { params, signal })).data;
    // The visitor token every public read is scoped by. Stored before any state is set, because
    // the very next fetch on boot is already a public one and would 401 without it.
    setCityToken(data.city_token, data.city_token_expires_in);
    setTenant(data.tenant);
    setTheme(data.theme);
    setMode(data.mode);
    setBusinessContext(data.business);
    setFeatures(data.app_config?.features || {});
    setPrimaryDomain(data.primary_domain || "");
    applyTenantTheme(data.theme);
    applyCityBranding(data.tenant, data.theme);
    localStorage.setItem(TENANT_KEY, data.tenant.slug);
    // `resolveImage` (`lib/media.js`) reads this to rewrite a LEGACY stored
    // `/api/media/<id>/<variant>` URL into the tokenless public form for the city being browsed.
    if (data.tenant?.id) localStorage.setItem(TENANT_ID_KEY, data.tenant.id);
    return data;
  }, []);

  // The api client cannot re-resolve the city itself — it has no idea which one is current — so
  // it calls back in here when a public read 401s. Returning the fresh token lets it retry the
  // request that failed, rather than surfacing a "choose a city" error the visitor cannot act on.
  useEffect(() => {
    onCityTokenExpired(async () => {
      const slug = localStorage.getItem(TENANT_KEY) || "";
      clearCityToken();
      const { data } = await api.get("/tenants/resolve", {
        params: slug
          ? { host: window.location.hostname, tenant: slug }
          : { host: window.location.hostname },
        // Without this a failed refresh pops the sign-in modal on top of the 401 it is trying to
        // resolve, which is the exact confusion this whole path exists to remove.
        skipAuthPrompt: true,
      });
      setCityToken(data.city_token, data.city_token_expires_in);
      return data.city_token || null;
    });
  }, []);

  // The city is the first path segment, so switching city means going to that city's address.
  const switchTenant = useCallback((slug) => {
    localStorage.setItem(TENANT_KEY, slug);
    // Drop the OUTGOING city's visitor token before the reload. It is still valid and still
    // signed — for the wrong city — so leaving it would let any request that fires before the new
    // resolve completes be scoped to the city the user just left. A cross-tenant read is exactly
    // what the token is supposed to prevent, so it must not survive a switch.
    clearCityToken();
    window.location.assign(`/${slug}`);
  }, []);

  const refreshCart = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setCart(null);
      return null;
    }
    try {
      const { data } = await api.get("/cart", { skipAuthPrompt: true });
      setCart(data);
      return data;
    } catch (e) {
      setCart(null);
      return null;
    }
  }, []);

  const setSession = useCallback((payload) => {
    localStorage.setItem(TOKEN_KEY, payload.access_token);
    setAuth(payload);
  }, []);

  const signOut = useCallback(() => {
    // Clear BOTH the session token and the city visitor token (F-07); the caller routes to the
    // signed-out city home, where the city token is re-minted on demand.
    localStorage.removeItem(TOKEN_KEY);
    clearCityToken();
    setAuth(null);
    setCart(null);
  }, []);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    try {
      const { data } = await api.get("/auth/me", { skipAuthPrompt: true });
      setAuth({ ...data, access_token: token });
      return data;
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setAuth(null);
      return null;
    }
  }, []);

  const switchRole = useCallback(async (roleProfileId) => {
    const { data } = await api.post("/auth/role/switch", {
      role_profile_id: roleProfileId,
    });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setAuth(data);
    return data;
  }, []);

  const retryBoot = useCallback(() => {
    setBooting(true);
    setBootError(false);
    setBootSlow(false);
    setBootAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // Render.com free-tier cold starts (and any slow network) can leave the boot resolve
    // pending for tens of seconds; without this the app hangs on a bare spinner forever.
    const slowTimer = setTimeout(() => {
      if (!cancelled) setBootSlow(true);
    }, 6000);

    // Declared OUTSIDE the try below so `finally` can await it. As a `const` inside the try it
    // is block-scoped, and the finally would throw ReferenceError on EVERY boot — the happy
    // path included — leaving every visitor on a spinner that never clears.
    let dictReady = Promise.resolve();

    (async () => {
      try {
        // In parallel with the first API call, not after it: the dictionary is a static asset on
        // the CDN and has nothing to wait for. Boot already makes four sequential round trips, so
        // riding alongside them makes the split effectively free in wall-clock terms.
        dictReady = loadDictionary(lang).then((d) => {
          if (!cancelled) setDict(d);
        });
        // The admin plane has NO city and NO cart. The citizen boot below (/tenants,
        // /tenants/resolve, /cart) is exactly the code `AdminHostMiddleware` refuses with a 404 on
        // the admin host (web/middleware.py) — and firing it here, then leaving `citySlug` unset,
        // dropped the SPA onto a broken citizen render after admin sign-in (the blank page). On the
        // admin host the session is established by Google sign-in alone (GoogleCallback) and every
        // admin read is a `/api/admin/*` call, so boot resolves to nothing but the dictionary. The
        // `finally` still loads it and clears `booting`; `citySlug` stays "" so `Shell` renders the
        // AdminPortal.
        // UAT AD1: restore the session from the stored token BEFORE returning. The early return
        // used to skip `refreshMe()`, so a reload / new tab on the admin host came back signed
        // out and `RequirePlatformAdmin` redirected to /login. `GET /auth/me` is allowed on the
        // admin host (web/middleware.py `_ADMIN_SIGNIN_ROUTES`); still no tenants, no cart.
        if (isAdminHost()) {
          await refreshMe();
          return;
        }
        // Boot's three calls are independent of each other except for one real dependency: the
        // city slug comes from `/tenants` + the URL. So (a) when the URL already names a city,
        // resolve it speculatively IN PARALLEL with `/tenants` (cold start was paid three times
        // in series; now once), and (b) restore the session (`/auth/me`) in parallel too -- it
        // needs only the stored token, not the city. A web-handoff arrival (no token yet, a
        // `?handoff=` code) keeps the old order: the code must be consumed BEFORE `/auth/me`.
        const segment0 = firstSegment();
        const specSlug =
          segment0 && !PLATFORM_PATHS.includes(segment0) ? segment0 : "";
        const specResolve = specSlug
          ? api
              .get("/tenants/resolve", {
                params: { host: window.location.hostname, tenant: specSlug },
                signal: controller.signal,
              })
              .then((r) => r.data)
              .catch(() => null)
          : Promise.resolve(null);
        const meEarly = localStorage.getItem(TOKEN_KEY) ? refreshMe() : null;
        const { data: list } = await api.get("/tenants", {
          signal: controller.signal,
        });
        if (cancelled) return;
        setTenants(list);
        let slug = resolveCityFromUrl(
          list.map((c) => c.slug),
          localStorage.getItem(TENANT_KEY),
        );
        // `resolveCityFromUrl` only recognizes a city that's in the ACTIVE-only `/tenants` list
        // above — so the instant a city is suspended, it drops out of that list and this whole
        // branch falls through to the platform chooser for every URL under that city's slug,
        // including its own admin/operator consoles, with no recovery path. `/tenants/resolve`
        // (what `loadTenant` calls) has no such active-status filter, so a URL whose first
        // segment looks like it was meant to be a city slug — not empty, not a reserved platform
        // path — gets one direct attempt there before giving up on it.
        if (!slug) {
          const segment = firstSegment();
          if (segment && !PLATFORM_PATHS.includes(segment)) {
            // The probe is the speculative resolve fired alongside `/tenants` above (same call,
            // same "only trust it if it describes the city asked for" rule), awaited here
            // instead of issued again.
            const spec = await specResolve;
            if (spec?.tenant?.slug === segment) slug = segment;
          }
        }
        setCitySlug(slug);
        if (!slug) return;
        await loadTenant(slug, controller.signal, await specResolve);
        if (cancelled) return;
        // BIZ-PS-03: the mobile app's "Edit my website" row opens this console with a one-time
        // `?handoff=` code — consumed here, before `refreshMe()`, so an owner with no stored web
        // token yet lands on `Guard` already authenticated instead of a redirect to `/login` with
        // the code silently going unused.
        await consumeWebHandoff();
        if (meEarly) await meEarly;
        else await refreshMe();
        await refreshCart();
      } catch (e) {
        if (!cancelled && e?.code !== "ERR_CANCELED") setBootError(true);
      } finally {
        // Never clear `booting` with no strings to render. A failed dictionary fetch still resolves
        // here: `translate` falls back to returning keys, which is degraded but navigable, and is
        // strictly better than a spinner that never ends.
        await dictReady.catch(() => {});
        clearTimeout(slowTimer);
        if (!cancelled) {
          setBootSlow(false);
          setBooting(false);
        }
      }
    })();
    document.documentElement.lang = lang;
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(slowTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootAttempt]);

  const value = useMemo(
    () => ({
      lang,
      changeLanguage,
      t,
      getCategories,
      tenants,
      tenant,
      citySlug,
      primaryDomain,
      theme,
      mode,
      businessContext,
      storefrontBranding,
      setStorefrontBranding,
      enquiryPrefill,
      setEnquiryPrefill,
      fulfillmentPrefill,
      setFulfillmentPrefill,
      features,
      switchTenant,
      auth,
      setSession,
      signOut,
      refreshMe,
      switchRole,
      cart,
      refreshCart,
      booting,
      bootSlow,
      bootError,
      retryBoot,
    }),
    [
      lang,
      changeLanguage,
      t,
      getCategories,
      tenants,
      tenant,
      citySlug,
      primaryDomain,
      theme,
      mode,
      businessContext,
      storefrontBranding,
      enquiryPrefill,
      fulfillmentPrefill,
      features,
      switchTenant,
      auth,
      setSession,
      signOut,
      refreshMe,
      switchRole,
      cart,
      refreshCart,
      booting,
      bootSlow,
      bootError,
      retryBoot,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
